import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import Alert from '@mui/material/Alert';
import { setFormButtonLoadingHasChanged } from '../../components/FormBuilderLibrary/slice';
import StyledWrapper from './style';

const FormValidator = ({ errors, formRef }) => {
	const dispatch = useDispatch();
	const errorSummaryRef = useRef(null);
	const hasScrolledToSummary = useRef(false);

	const getScrollableContainer = useCallback((element) => {
		let current = element?.parentElement;
		while (current) {
			const style = window.getComputedStyle(current);
			const overflowY = style.overflowY;
			const canScroll =
				(overflowY === 'auto' || overflowY === 'scroll') &&
				current.scrollHeight > current.clientHeight;
			if (canScroll) return current;
			current = current.parentElement;
		}
		return window;
	}, []);

	const scrollToElement = useCallback(
		(element, offset = 64) => {
			if (!element) return;

			const container = getScrollableContainer(element);
			const elementRect = element.getBoundingClientRect();

			if (container === window) {
				const scrollTop = elementRect.top + window.scrollY - offset;
				window.scrollTo({ top: scrollTop, behavior: 'smooth' });
			} else {
				const containerRect = container.getBoundingClientRect();
				const scrollTop = elementRect.top - containerRect.top + container.scrollTop - offset;
				container.scrollTo({ top: scrollTop, behavior: 'smooth' });
			}
		},
		[getScrollableContainer]
	);

	const scrollToElementWithOffset = useCallback(
		(name, offset = 64) => {
			const element =
				formRef?.current?.querySelector(`[name="${name}"]`) ||
				document.querySelector(`[name="${name}"]`);

			if (!element) {
				console.warn(`Could not find element with name: ${name}`);
				return;
			}

			scrollToElement(element, offset);
		},
		[formRef, scrollToElement]
	);

	useEffect(() => {
		const hasErrors = errors?.length > 0;

		if (hasErrors) {
			dispatch(setFormButtonLoadingHasChanged(false));

			if (errorSummaryRef.current && !hasScrolledToSummary.current) {
				scrollToElement(errorSummaryRef.current);
				hasScrolledToSummary.current = true;
			}
		} else {
			hasScrolledToSummary.current = false;
		}
	}, [errors, dispatch, scrollToElement]);

	return (
		<div>
			{errors?.length > 0 && (
				<Alert ref={errorSummaryRef} style={{ marginBottom: '30px' }} severity="error">
					<StyledWrapper.ValidatorTitle>This form contains errors.</StyledWrapper.ValidatorTitle>
					<br />
					Please review the following fields:
					<StyledWrapper.ErrorList>
						{errors.map((error, idx) => (
							<li key={idx}>
								<StyledWrapper.ErrorLabel
									onClick={() => {
										scrollToElementWithOffset(error.field_name);
									}}
								>
									{error.label}
								</StyledWrapper.ErrorLabel>{' '}
								- {error.error}
							</li>
						))}
					</StyledWrapper.ErrorList>
				</Alert>
			)}
		</div>
	);
};

export default FormValidator;
